import { useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Sidebar from './Sidebar';
import Header from './Header';
import ErrorBoundary from '../Common/ErrorBoundary';
import ScreenAccessGate from './ScreenAccessGate';
import { useRealtime } from '../../hooks/useRealtime';
import { useShellDocumentTitle } from '../../hooks/useDocumentTitle';
import { useShellSidebar } from '../../canvas/dc/boardChrome';
import { navigationGroups } from '../../config/navigation';
import { screenDefinitions } from '../../config/screenRegistry';
import { screens } from '@screens';
import '../../canvas/tokens.css';
import '../../canvas/chrome.css';

/**
 * Authenticated web chrome: grouped canvas sidebar (fixed at 1024px and up, a drawer below),
 * canvas top bar, and the routed page. The sidebar scrolls on its own; the page scrolls the window.
 */
/** Route patterns of implemented (non-shell) pages; they expect the layout to pad them. */
const implementedPatterns = [
  ...new Set([
    ...navigationGroups.flatMap((g) => g.items.map((i) => i.href)),
    ...screens.flatMap((s) => (s.wiredWeb ? [s.wiredWeb] : [])),
    ...screenDefinitions.map((d) => d.path),
    '/orders/new',
    '/inventory/new',
    '/inventory/lots',
  ]),
].filter((p) => p !== '/');

function isImplementedPage(pathname: string): boolean {
  return implementedPatterns.some((p) => matchPath({ path: p, end: true }, pathname) !== null);
}

export default function DashboardLayout() {
  // a 1:1 design board brings the canvas chrome itself (its own left navigation, the shared TopNav,
  // the skip link): the shell then draws none of its own and the board renders full-bleed
  const { isCollapsed, onDesignBoard } = useShellSidebar();
  const location = useLocation();
  const implemented = useMemo(() => isImplementedPage(location.pathname), [location.pathname]);
  const padded = implemented && !onDesignBoard;
  const chrome = !onDesignBoard;

  useRealtime({ showNotifications: true });
  useShellDocumentTitle();

  // the chrome slots stay in place (null) so the routed page is not remounted when a board registers
  return (
    <div className="min-h-screen bg-stone-50">
      {chrome ? (
        <a
          href="#main-content"
          className="sr-only z-[100] rounded-md bg-white px-4 py-2 font-medium text-stone-900 shadow focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
        >
          Skip to main content
        </a>
      ) : null}

      {chrome ? <Sidebar /> : null}

      <div className={!chrome ? undefined : isCollapsed ? 'lg:pl-16' : 'lg:pl-72'}>
        {chrome ? <Header /> : null}

        <main id="main-content" tabIndex={-1} className={padded ? 'p-4 outline-none lg:p-6' : 'outline-none'}>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ErrorBoundary key={location.pathname}>
              <ScreenAccessGate />
            </ErrorBoundary>
          </motion.div>
        </main>
      </div>
    </div>
  );
}
